const scenePattern = /^(INT\.?|EXT\.?|INT\.\/?EXT\.?|EXT\.\/?INT\.?|SCENE\s+\d+|ACT\s+[IVX\d]+)\b/i;
const uppercaseCharacterPattern = /^[A-Z][A-Z0-9 ._'’\-]{0,38}(?:\s*\([^)]*\))?$/;
const transitionPattern = /^(CUT TO:|FADE (IN|OUT)\.?|DISSOLVE TO:|SMASH CUT TO:|THE END|LATER|CONTINUED)$/i;
const characterSuffixPattern = /\s*\((V\.?(?:\s*O\.?)?|O\.?(?:\s*S\.?)?|CONT['’]?D|CONTINUED)\)\s*$/i;
const uppercaseActionVerbPattern = /\b(APPEARS?|CLOSES?|CONTINUES?|CROSSES?|DISAPPEARS?|ECHOES?|ENTERS?|EXITS?|FALLS?|FLICKERS?|LIGHTS?|LOOKS?|MOVES?|OPENS?|REACHES?|RINGS?|RISES?|RUNS?|SITS?|SOUNDS?|STANDS?|TURNS?|WALKS?)\b/;

function clean(value, limit = 4000) {
  return String(value ?? '').replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '').trim().slice(0, limit);
}

export function characterKey(name) {
  return clean(name, 80).replace(characterSuffixPattern, '').replace(/\s+/g, ' ').toUpperCase();
}

function characterIdFor(name, usedIds) {
  const base = characterKey(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'character';
  let candidate = base;
  let suffix = 2;
  while (usedIds.has(candidate)) candidate = `${base}-${suffix++}`;
  usedIds.add(candidate);
  return candidate;
}

function looksLikeTitleCaseName(line) {
  const base = line.replace(characterSuffixPattern, '').trim();
  if (!base || base.length > 40 || /[!?;:]$/.test(base) || /\d/.test(base)) return false;
  const words = base.split(/\s+/);
  if (words.length > 6) return false;
  return words.every((word) => /^(?:[A-Z][\p{L}'’.-]*|Dr\.|Mr\.|Mrs\.|Ms\.|Officer|Captain|Doctor)$/u.test(word));
}

function assessCharacterHeading(line) {
  if (transitionPattern.test(line) || scenePattern.test(line)) return { matches: false, confidence: 0 };
  if (uppercaseCharacterPattern.test(line) && line.length < 48) {
    const base = line.replace(characterSuffixPattern, '').trim();
    if (/[.!?;:]$/.test(base) || (base.split(/\s+/).length > 1 && uppercaseActionVerbPattern.test(base))) return { matches: false, confidence: 0 };
    return { matches: true, confidence: 0.98, style: 'uppercase' };
  }
  if (looksLikeTitleCaseName(line)) return { matches: true, confidence: 0.68, style: 'title-case', reason: 'This title-case line might be a character name or ordinary script text.' };
  return { matches: false, confidence: 0 };
}

function normalizeDirection(line) {
  const value = clean(line.replace(/^[([]|[)\]]$/g, ''), 200000);
  if (!value) return '';
  return `${value[0].toUpperCase()}${value.slice(1)}${/[.!?]$/.test(value) ? '' : '.'}`;
}

export function renameParsedCharacter(parsed, characterId, nextName) {
  const name = clean(nextName, 60);
  if (!parsed || !name) return parsed;
  return {
    ...parsed,
    characters: (parsed.characters || []).map((character) => character.id === characterId ? { ...character, name, confidence: 1, reviewReason: 'Name checked by you.' } : character),
    entries: (parsed.entries || []).map((entry) => entry.characterId === characterId ? { ...entry, speaker: name } : entry),
  };
}

export function addParsedCharacter(parsed, requestedName = 'New character') {
  if (!parsed) return parsed;
  const usedIds = new Set((parsed.characters || []).map((character) => character.id));
  const name = clean(requestedName, 60) || 'New character';
  return {
    ...parsed,
    characters: [...(parsed.characters || []), { id: characterIdFor(name, usedIds), name, confidence: 1, reviewReason: 'Added by you.', headingCount: 0 }],
  };
}

export function removeParsedCharacter(parsed, characterId) {
  if (!parsed) return parsed;
  const character = (parsed.characters || []).find((item) => item.id === characterId);
  return {
    ...parsed,
    characters: (parsed.characters || []).filter((item) => item.id !== characterId),
    entries: (parsed.entries || []).map((entry) => entry.characterId === characterId ? {
      ...entry, type: 'direction', characterId: undefined, speaker: undefined, confidence: 0.55,
      reviewReason: `${character?.name || 'The removed character'} was removed. Confirm whether this text is a stage direction or should be ignored.`,
    } : entry),
  };
}

export function summarizeImportReview(parsed, threshold = 0.8) {
  const uncertainCharacters = (parsed?.characters || []).map((character, index) => ({ character, index })).filter(({ character }) => Number(character.confidence ?? 1) < threshold);
  const uncertainScenes = (parsed?.scenes || []).map((scene, index) => ({ scene, index })).filter(({ scene }) => Number(scene.confidence ?? 1) < threshold);
  const uncertainEntries = (parsed?.entries || []).map((entry, index) => ({ entry, index })).filter(({ entry }) => Number(entry.confidence ?? 1) < threshold || (entry.type === 'dialogue' && !entry.characterId));
  return {
    uncertainCharacters, uncertainScenes, uncertainEntries,
    needsReview: uncertainCharacters.length + uncertainScenes.length + uncertainEntries.length,
    totalBlocks: (parsed?.entries || []).length,
  };
}

export function parseScript(source) {
  const original = String(source || '').replace(/\r/g, '').slice(0, 200000);
  const rawLines = original.split('\n');
  const scenes = [];
  const charactersByKey = new Map();
  const usedIds = new Set();
  const entries = [];
  let sceneId = 'scene-1';
  let currentCharacter = null;
  let dialogueEntry = null;
  if (!rawLines.some((line) => scenePattern.test(line.trim()))) scenes.push({ id: sceneId, heading: 'SCENE 1', confidence: 0.45, reviewReason: 'No scene heading was found. Rename this scene if needed.' });

  function useCharacterHeading(line, assessment = assessCharacterHeading(line)) {
    const key = characterKey(line);
    let character = charactersByKey.get(key);
    if (!character) {
      character = {
        id: characterIdFor(key, usedIds), name: titleCase(key), confidence: assessment.confidence,
        reviewReason: assessment.reason || '', headingStyle: assessment.style, headingCount: 1,
      };
      charactersByKey.set(key, character);
    } else {
      character.headingCount = (character.headingCount || 0) + 1;
      character.confidence = Math.max(character.confidence || 0, assessment.confidence || 0);
    }
    currentCharacter = character;
    dialogueEntry = null;
  }

  for (const rawLine of rawLines) {
    const line = rawLine.replace(/\t/g, ' ').trim();
    if (!line) {
      if (dialogueEntry) currentCharacter = null;
      dialogueEntry = null;
      continue;
    }
    if (scenePattern.test(line)) {
      sceneId = `scene-${scenes.length + 1}`;
      scenes.push({ id: sceneId, heading: line, confidence: 0.99, reviewReason: '' });
      entries.push({ type: 'scene', sceneId, text: line, confidence: 0.99, reviewReason: '' });
      currentCharacter = null;
      dialogueEntry = null;
      continue;
    }
    if (transitionPattern.test(line)) {
      entries.push({ type: 'transition', sceneId, text: line, confidence: 0.99, reviewReason: '' });
      currentCharacter = null;
      dialogueEntry = null;
      continue;
    }
    if (/^\(.+\)$/.test(line) || /^\[.+\]$/.test(line)) {
      entries.push({ type: 'direction', sceneId, characterId: currentCharacter?.id, speaker: currentCharacter?.name, text: normalizeDirection(line), confidence: 0.97, reviewReason: '' });
      dialogueEntry = null;
      continue;
    }
    if (currentCharacter) {
      const possibleNextHeading = assessCharacterHeading(line);
      if (possibleNextHeading.matches && possibleNextHeading.style === 'uppercase') {
        // PDF extraction often omits blank separators. Keep a new speaker distinct,
        // while flagging the ambiguous unseparated heading for human review.
        useCharacterHeading(line, dialogueEntry ? {
          ...possibleNextHeading, confidence: 0.75,
          reason: 'No blank line separates this possible speaker from dialogue. Check the name and lines.',
        } : possibleNextHeading);
        continue;
      }
      if (dialogueEntry && dialogueEntry.characterId === currentCharacter.id && dialogueEntry.sceneId === sceneId) dialogueEntry.text = `${dialogueEntry.text} ${line}`.trim();
      else {
        dialogueEntry = { type: 'dialogue', sceneId, characterId: currentCharacter.id, speaker: currentCharacter.name, text: clean(line, 200000), confidence: 0.98, reviewReason: '' };
        entries.push(dialogueEntry);
      }
      continue;
    }
    const characterHeading = assessCharacterHeading(line);
    if (characterHeading.matches) {
      useCharacterHeading(line, characterHeading);
      continue;
    }
    const confidentDirection = /[.!?]$/.test(line) || uppercaseActionVerbPattern.test(line);
    entries.push({
      type: 'direction', sceneId, text: normalizeDirection(line), confidence: confidentDirection ? 0.9 : 0.64,
      reviewReason: confidentDirection ? '' : 'This unformatted line might be a stage direction, dialogue, or a note.',
    });
  }
  const characters = [...charactersByKey.values()];
  const dialogueCounts = new Map();
  for (const entry of entries) if (entry.type === 'dialogue' && entry.characterId) dialogueCounts.set(entry.characterId, (dialogueCounts.get(entry.characterId) || 0) + 1);
  for (const character of characters) {
    const dialogueCount = dialogueCounts.get(character.id) || 0;
    character.dialogueCount = dialogueCount;
    if (!dialogueCount) {
      character.confidence = 0.25;
      character.reviewReason = 'No dialogue was found beneath this possible character name. Remove it if it is a note or heading.';
    } else if (character.headingStyle === 'title-case' && character.headingCount > 1) {
      character.confidence = 0.86;
      character.reviewReason = '';
    }
  }
  const orderedNames = [...characters].sort((left, right) => right.name.length - left.name.length);
  for (const entry of entries) {
    if (entry.type !== 'direction' || entry.characterId) continue;
    const owner = orderedNames.find((character) => new RegExp(`^${escapeRegExp(character.name)}(?:\\s|[,:—-])`, 'i').test(entry.text));
    if (owner) { entry.characterId = owner.id; entry.speaker = owner.name; entry.confidence = Math.max(entry.confidence || 0, 0.9); entry.reviewReason = ''; }
  }
  return { schemaVersion: 3, original, scenes, characters, entries };
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function titleCase(text) {
  return text.toLowerCase().replace(/(^|[\s'-])\p{L}/gu, (match) => match.toUpperCase());
}

export function fadeLine(text, level) {
  const words = String(text).split(/\s+/);
  if (level <= 1) return text;
  if (level === 2) return words.map((word, index) => index % 3 === 2 ? '••••' : word).join(' ');
  if (level === 3) return words.slice(0, Math.max(1, Math.ceil(words.length / 3))).join(' ') + ' …';
  if (level === 4) return words.map((word) => word[0]).join(' · ');
  if (level === 5) return 'Cue only — trust the moment.';
  return '';
}
